/**
 * Volume Tier Hooks
 *
 * TanStack Query hooks for volume pricing tiers.
 * Fetches and saves quantity-based price tiers (Pricing Rules)
 * from zaviago_backend.api.pricing_promotion_actions endpoints.
 *
 * Part of Plan 45 (Volume Pricing), Epic 02.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// ── Types ────────────────────────────────────────────────────

export interface VolumeTier {
  name: string;
  min_qty: number;
  max_qty: number | null; // null = unlimited (last tier)
  rate: number;
  title: string;
}

// ── Query Keys ───────────────────────────────────────────────

export const volumeTierKeys = {
  tiers: (itemCode: string) => ["volume-tiers", itemCode] as const,
};

// ── Hooks ────────────────────────────────────────────────────

/**
 * Fetch volume pricing tiers for an item.
 *
 * Returns sorted array of tiers (by min_qty ascending).
 * Each tier maps to a Pricing Rule with rate_or_discount="Rate",
 * no valid_upto (permanent).
 */
export function useVolumeTiers(itemCode: string) {
  return useQuery<VolumeTier[]>({
    queryKey: volumeTierKeys.tiers(itemCode),
    enabled: !!itemCode && itemCode !== "new",
    queryFn: () =>
      frappeMethod<VolumeTier[]>(
        "zaviago_backend.api.pricing_promotion_actions.get_volume_tiers",
        { item_code: itemCode }
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Mutation to atomically save volume pricing tiers.
 *
 * Sends the full tier array. Backend reconciles: creates new,
 * updates changed, deletes removed.
 *
 * Invalidates volume-tiers, pricing-summary, and item-pricing-rules
 * query keys on success.
 */

interface SaveVolumeTiersParams {
  item_code: string;
  tiers: { min_qty: number; max_qty: number | null; rate: number }[];
}

export function useSaveVolumeTiers(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<VolumeTier[], Error, SaveVolumeTiersParams>({
    mutationFn: (params) =>
      frappeMethod<VolumeTier[]>(
        "zaviago_backend.api.pricing_promotion_actions.save_volume_tiers",
        params
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volume-tiers", itemCode] });
      queryClient.invalidateQueries({ queryKey: ["pricing-summary", itemCode] });
      queryClient.invalidateQueries({ queryKey: ["item-pricing-rules", itemCode] });
    },
  });
}
